import { useState, useCallback } from 'react';
import { ErrorTypes } from '../Restclient/axiosConfig';

/**
 * Enhanced API handler with CustomDialogBox support only
 * 
 * @param {Object} customMessages - Custom error messages per error type
 * @param {Object} options - Configuration options
 * @returns {Object} - { loading, error, callApi, clearError, dialogState, openDialog, closeDialog }
 */
export const useApiHandler = (customMessages = {}, options = {}) => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [dialogState, setDialogState] = useState({
        open: false,
        title: '',
        message: '',
        type: 'Info', // Info, Success, Warning, Error
        confirmText: 'OK',
        cancelText: 'Cancel',
        showCancel: false,
        onConfirm: null,
        onCancel: null
    });

    const {
        showDialogOnSuccess = false,      // Don't show dialog on success by default
        showDialogOnError = false,        // Don't show dialog on error by default (developer controlled)
        autoCloseDialog = false,
        autoCloseDelay = 3000
    } = options;

    // Default messages
    const defaultMessages = {
        [ErrorTypes.UNAUTHORIZED]: 'Your session has expired. Please login again.',
        [ErrorTypes.FORBIDDEN]: 'You do not have permission to perform this action.',
        [ErrorTypes.NOT_FOUND]: 'The requested resource was not found.',
        [ErrorTypes.BAD_REQUEST]: 'Invalid request. Please check your input.',
        [ErrorTypes.SERVER_ERROR]: 'Server error. Please try again later.',
        [ErrorTypes.NETWORK_ERROR]: 'Network error. Please check your connection.',
        [ErrorTypes.TIMEOUT]: 'Request timeout. Please try again.',
        [ErrorTypes.UNKNOWN]: 'An unexpected error occurred.',
        ...customMessages
    };

    /**
     * Open dialog manually
     */
    const openDialog = useCallback((config) => {
        setDialogState(prev => ({
            ...prev,
            open: true,
            ...config
        }));

        if (autoCloseDialog) {
            setTimeout(() => {
                closeDialog();
            }, autoCloseDelay);
        }
    }, [autoCloseDialog, autoCloseDelay]);

    /**
     * Close dialog
     */
    const closeDialog = useCallback(() => {
        setDialogState(prev => ({ ...prev, open: false }));
    }, []);

    /**
     * Get dialog type based on error type
     */
    const getDialogType = (errorType) => {
        const typeMap = {
            [ErrorTypes.UNAUTHORIZED]: 'Warning',
            [ErrorTypes.FORBIDDEN]: 'Warning',
            [ErrorTypes.NOT_FOUND]: 'Info',
            [ErrorTypes.BAD_REQUEST]: 'Warning',
            [ErrorTypes.SERVER_ERROR]: 'Error',
            [ErrorTypes.NETWORK_ERROR]: 'Error',
            [ErrorTypes.TIMEOUT]: 'Warning'
        };
        return typeMap[errorType] || 'Error';
    };

    /**
     * Call API with dialog control
     * 
     * @param {Function} apiFunction - The API function to call
     * @param {Object} callOptions - Configuration for this specific call
     */
    const callApi = useCallback(async (apiFunction, callOptions = {}) => {
        const {
            onSuccess = null,
            onError = null,
            onCatchError = null,  // NEW: Separate callback for catch block errors
            successMessage = null,
            successTitle = 'Success',
            customErrorMessages = {},
            showDialog = null,                      // Boolean: true/false to show/hide dialog
            showDialogOnStatusCodes = null,        // Array: [401, 403, 500] - show dialog for these codes
            hideDialogOnStatusCodes = null,        // Array: [404] - hide dialog for these codes
            showDialogOnCatch = null,              // NEW: Boolean to control dialog for catch errors
            useBackendMessage = false,             // Boolean: use message from backend directly
            dialogConfig = {}                       // Custom dialog configuration
        } = callOptions;

        setLoading(true);
        setError(null);

        try {
            const response = await apiFunction();
            if (response?.success || response.data?.success) {
                // SUCCESS HANDLING
                const finalSuccessMessage = successMessage || response.data.message;
                
                // Determine if dialog should be shown on success
                const shouldShowSuccessDialog = showDialog !== null 
                    ? showDialog 
                    : showDialogOnSuccess;

                if (shouldShowSuccessDialog) {
                    openDialog({
                        title: successTitle,
                        message: finalSuccessMessage,
                        type: 'Success',
                        confirmText: 'OK',
                        showCancel: false,
                        onConfirm: () => {
                            closeDialog();
                            if (onSuccess) {
                                onSuccess(response, finalSuccessMessage);
                            }
                        },
                        ...dialogConfig
                    });
                } else if (onSuccess) {
                    onSuccess(response, finalSuccessMessage);
                }

                setLoading(false);
                return {
                    success: true,
                    data: response.data,
                    message: finalSuccessMessage
                };

            } else {
                // ERROR HANDLING
                // Determine message source
               
                let errorMessage;
                if (useBackendMessage) {
                    // Use backend message directly
                    errorMessage = response.message;
                } else {
                    // Use custom message or default
                    errorMessage = customErrorMessages[response.errorType] 
                        || defaultMessages[response.errorType] 
                        || response.message;
                }

                const errorInfo = {
                    type: response.errorType,
                    message: errorMessage,
                    code: response.code,
                    details: response.errorDetails
                };

                setError(errorInfo);

                // Determine if dialog should be shown
                let shouldShowErrorDialog = showDialog !== null 
                    ? showDialog 
                    : showDialogOnError;

                // Override based on status codes
                if (showDialogOnStatusCodes && showDialogOnStatusCodes.includes(response.code)) {
                    shouldShowErrorDialog = true;
                }
                if (hideDialogOnStatusCodes && hideDialogOnStatusCodes.includes(response.code)) {
                    shouldShowErrorDialog = false;
                }

                if (shouldShowErrorDialog) {
                    openDialog({
                        title: response.errorDetails?.title || 'Error',
                        message: errorMessage,
                        type: getDialogType(response.errorType),
                        confirmText: 'OK',
                        showCancel: false,
                        onConfirm: () => {
                            closeDialog();
                            if (onError) {
                                onError(response, errorInfo);
                            }
                        },
                        ...dialogConfig
                    });
                } else if (onError) {
                    onError(response, errorInfo);
                }

                setLoading(false);
                return {
                    success: false,
                    error: errorMessage,
                    errorType: response.errorType,
                    code: response.code
                };
            }
        } catch (err) {

            const errorMessage = err?.response?.data?.message || 'An unexpected error occurred';
            const errorInfo = {
                type: ErrorTypes.UNKNOWN,
                message: errorMessage,
                code: 0,
                details: err
            };

            setError(errorInfo);

            // Determine if dialog should be shown for catch block errors
            const shouldShowCatchDialog = showDialogOnCatch !== null 
                ? showDialogOnCatch 
                : (showDialog !== null ? showDialog : true); // Default to true for catch errors

            if (shouldShowCatchDialog) {
                openDialog({
                    title: 'Unexpected Error',
                    message: errorMessage,
                    type: 'Error',
                    confirmText: 'OK',
                    showCancel: false,
                    onConfirm: () => {
                        closeDialog();
                        // Call the appropriate callback
                        if (onCatchError) {
                            onCatchError({ success: false, message: errorMessage }, errorInfo);
                        } else if (callOptions.onError) {
                            callOptions.onError({ success: false, message: errorMessage }, errorInfo);
                        }
                    },
                    ...dialogConfig
                });
            } else {
                // No dialog, just call callbacks
                if (onCatchError) {
                    onCatchError({ success: false, message: errorMessage }, errorInfo);
                } else if (callOptions.onError) {
                    callOptions.onError({ success: false, message: errorMessage }, errorInfo);
                }
            }

            setLoading(false);
            return {
                success: false,
                error: errorMessage,
                errorType: ErrorTypes.UNKNOWN
            };
        }
    }, [defaultMessages, showDialogOnSuccess, showDialogOnError, openDialog, closeDialog]);

    const clearError = useCallback(() => {
        setError(null);
    }, []);

    return { 
        loading, 
        error, 
        callApi, 
        clearError, 
        dialogState, 
        openDialog, 
        closeDialog 
    };
};
